import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { MarketplacePlugin } from './types';
import { getManageIntegrationSecrets } from 'store/authentication/selectors';

export const useMarketplacePermissions = (
  marketplacePlugin?: MarketplacePlugin,
  businessPluginApp?: { source?: string }
) => {
  const auth = useSelector((s: any) => s.authentication);
  const manageIntegrationSecretsAccess = useSelector(
    getManageIntegrationSecrets
  );

  const canEditJSON =
    auth.access?.sections?.marketplace_section?.advanced_configuration?.edit;
  const canEditPlugins =
    businessPluginApp?.source === 'business'
      ? auth.access?.sections?.marketplace_section?.business_app_developer
          ?.remove
      : auth.access?.sections?.marketplace_section?.manage_apps?.edit;
  const canViewPlugins =
    auth.access?.sections?.marketplace_section?.manage_apps?.view;

  const isReadOnly = !canEditPlugins;

  const hasSetupAssistant = Boolean(
    marketplacePlugin?.base_config?.setup_assistant
  );

  const showConfigPanel = useCallback(
    (businessPluginApp: any) => {
      const hasBusinessPluginApp = Boolean(businessPluginApp);

      return (
        hasBusinessPluginApp &&
        (canEditJSON || hasSetupAssistant) &&
        marketplacePlugin?.base_config?.user_configurable !== false &&
        !isReadOnly
      );
    },
    [canEditJSON, hasSetupAssistant, isReadOnly, marketplacePlugin]
  );

  const showEditConfigurationButton =
    !isReadOnly && (canEditJSON || hasSetupAssistant);

  const canEditIntegrationSecrets = manageIntegrationSecretsAccess.edit;
  const canViewIntegrationSecrets = manageIntegrationSecretsAccess.view;

  const showManageIntegrationSecretsButton = useCallback(
    (businessPluginApp: any) => {
      const hasBusinessPluginApp = Boolean(businessPluginApp);

      return (
        hasBusinessPluginApp &&
        marketplacePlugin?.base_config?.secrets?.length &&
        (canEditIntegrationSecrets || canViewIntegrationSecrets)
      );
    },
    [canEditIntegrationSecrets, canViewIntegrationSecrets, marketplacePlugin]
  );

  return {
    canEditJSON,
    isReadOnly,
    canEditPlugins,
    canViewPlugins,
    showConfigPanel,
    showEditConfigurationButton,
    canEditIntegrationSecrets,
    canViewIntegrationSecrets,
    showManageIntegrationSecretsButton,
  };
};
